import { useCallback, useState, type CSSProperties } from 'react';
import { useI18n } from '@/hooks/useI18n';
import { useAuth } from '@/hooks/useAuth';
import { AcademiaPublisher } from './components/AcademiaPublisher';
import { CatalogPublisher } from './components/CatalogPublisher';
import { SocialHandoffForm } from './components/SocialHandoffForm';

export type PublisherLevel = 'home' | 'kinds' | 'form';
export type PublisherDirection = 'academia' | 'catalog' | 'social' | null;

export interface PublisherNavState {
  level: PublisherLevel;
  direction: PublisherDirection;
  kind: string | null;
}

export interface PublisherFormDraft {
  title?: string;
  summary?: string;
  body?: string;
  hashtags?: string[];
  pollOptions?: string[];
  officialUrl?: string;
  meta?: Record<string, string>;
  description?: string;
  participants?: string;
  eventDate?: string;
  captionDraft?: string;
  channels?: string[];
  materialType?: string;
  attachments?: Array<{ file_id: string; name: string; size?: number; mime?: string; purpose?: string }>;
}

export const EMPTY_PUBLISHER_NAV: PublisherNavState = {
  level: 'home',
  direction: null,
  kind: null,
};

interface KindCard {
  id: string;
  title: string;
  desc: string;
  color: string;
}

const ACADEMIA_KINDS: KindCard[] = [
  {
    id: 'NEWS',
    title: 'Новость',
    desc: 'Актуальная информация для ленты ACADEMIA',
    color: '#41d2ff',
  },
  {
    id: 'QUICK_POLL',
    title: 'Быстрый опрос',
    desc: 'Опрос с вариантами ответа — только для владельца',
    color: '#9e80ff',
  },
  {
    id: 'ACHIEVEMENT',
    title: 'Достижение',
    desc: 'Научные и образовательные достижения участников',
    color: '#49ddb7',
  },
  {
    id: 'EVENT',
    title: 'Событие',
    desc: 'Мероприятие, дедлайн или важная дата',
    color: '#e7b13a',
  },
];

const CATALOG_KINDS: KindCard[] = [
  {
    id: 'COMPETITION',
    title: 'Конкурс',
    desc: 'Конкурс, грант или программа с дедлайном',
    color: '#f97316',
  },
  {
    id: 'MATERIAL',
    title: 'Материал',
    desc: 'Учебный или методический ресурс для каталога',
    color: '#41d2ff',
  },
  {
    id: 'EDUCATION',
    title: 'Образование',
    desc: 'Курс, программа или образовательное предложение',
    color: '#49ddb7',
  },
  {
    id: 'VACANCY',
    title: 'Вакансия',
    desc: 'Научная или преподавательская вакансия',
    color: '#e7b13a',
  },
];

const SOCIAL_KINDS: KindCard[] = [
  {
    id: 'PUBLICATION',
    title: 'Контент для соцсетей / сайт ENU',
    desc: 'Передайте материал SMM-менеджеру для Instagram, сайта или Telegram',
    color: '#e2ad3a',
  },
];

/** Climb one Publisher level; returns null when already at home (caller → Hub). */
export function climbPublisherNav(nav: PublisherNavState): PublisherNavState | null {
  if (nav.level === 'form') {
    if (nav.direction === 'social') {
      return { level: 'home', direction: null, kind: null };
    }
    return { level: 'kinds', direction: nav.direction, kind: null };
  }
  if (nav.level === 'kinds') {
    return { level: 'home', direction: null, kind: null };
  }
  return null;
}

interface PublisherHubProps {
  /** Controlled nav (Create Request CONTENT route). */
  nav?: PublisherNavState;
  onNavChange?: (nav: PublisherNavState) => void;
  formDraft?: PublisherFormDraft;
  onFormDraftChange?: (patch: PublisherFormDraft) => void;
  /** When true, chrome Back is owned by parent overlay. */
  embeddedInGateway?: boolean;
}

export const PublisherHub = ({
  nav: controlledNav,
  onNavChange,
  formDraft,
  onFormDraftChange,
  embeddedInGateway = false,
}: PublisherHubProps) => {
  const { t } = useI18n();
  const { hasRole } = useAuth();
  const isOwner = hasRole('SYSTEM_OWNER');

  const [localNav, setLocalNav] = useState<PublisherNavState>(EMPTY_PUBLISHER_NAV);
  const [toastMsg, setToastMsg] = useState('');

  const isControlled = controlledNav != null && typeof onNavChange === 'function';
  const nav = isControlled ? controlledNav! : localNav;

  const setNav = useCallback(
    (next: PublisherNavState) => {
      if (isControlled) onNavChange!(next);
      else setLocalNav(next);
    },
    [isControlled, onNavChange]
  );

  const showToast = (msg: string) => {
    setToastMsg(msg);
    setTimeout(() => setToastMsg(''), 3000);
  };

  const directions = [
    {
      id: 'academia' as const,
      no: '01',
      label: t('publisher.hub.direction.academia' as any),
      desc: t('publisher.hub.direction.academia.desc' as any),
      color: '#41d2ff',
      chips: ['Новость', 'Опрос', 'Достижение', 'Событие'],
      icon: 'M4 5h16v14H4zM7 9h10M7 13h6M16 13h1M7 17h8',
    },
    {
      id: 'catalog' as const,
      no: '02',
      label: t('publisher.hub.direction.catalog' as any),
      desc: t('publisher.hub.direction.catalog.desc' as any),
      color: '#f97316',
      chips: ['Конкурс', 'Материал', 'Образование', 'Вакансия'],
      icon: 'M4 4h7l2 3h7v13H4V4zM8 12h8M12 9v6',
    },
    {
      id: 'social' as const,
      no: '03',
      label: t('publisher.hub.direction.social' as any),
      desc: t('publisher.hub.direction.social.desc' as any),
      color: '#e2ad3a',
      chips: ['Instagram', 'Сайт ENU', 'Telegram'],
      icon: 'M12 3l2.5 5.2 5.7.8-4.1 4 1 5.7L12 16.2 6.9 18.7l1-5.7-4.1-4 5.7-.8L12 3z',
    },
  ];

  const openDirection = (dir: Exclude<PublisherDirection, null>) => {
    // Three independent directions — never merge into one shared form.
    // Social has a single handoff kind and opens its own form immediately.
    if (dir === 'social') {
      setNav({ level: 'form', direction: 'social', kind: 'PUBLICATION' });
      return;
    }
    setNav({ level: 'kinds', direction: dir, kind: null });
  };

  const openKind = (kindId: string) => {
    setNav({ ...nav, level: 'form', kind: kindId });
  };

  const handleBack = () => {
    const next = climbPublisherNav(nav);
    if (next) setNav(next);
  };

  const kindCards = (): KindCard[] => {
    if (nav.direction === 'academia') {
      return ACADEMIA_KINDS.filter((k) => k.id !== 'QUICK_POLL' || isOwner);
    }
    if (nav.direction === 'catalog') return CATALOG_KINDS;
    if (nav.direction === 'social') return SOCIAL_KINDS;
    return [];
  };

  const directionLabel = directions.find((d) => d.id === nav.direction)?.label;
  const { level, direction, kind } = nav;

  return (
    <div className="rg-publisher">
      {level === 'home' ? (
        <div className="rg-publisher-intro">
          <small>ACADEMIA · PUBLISHER 1.81</small>
          <h3>{t('publisher.hub.title' as any)}</h3>
          <p>{t('publisher.hub.subtitle' as any)}</p>
        </div>
      ) : null}

      {level === 'home' ? (
        <div className="rg-route-home-grid rg-route-home-grid--publisher">
          {directions.map((dir) => (
            <button
              key={dir.id}
              type="button"
              onClick={() => openDirection(dir.id)}
              className="rg-route-home-card rg-route-home-card--xl rg-route-home-card--publisher"
              style={{ '--rg-accent': dir.color } as CSSProperties}
              data-u181-direction={dir.id}
            >
              <span className="rg-route-home-icon" aria-hidden="true">
                <svg viewBox="0 0 24 24">
                  <path d={dir.icon} />
                </svg>
              </span>
              <small className="rg-route-home-num">{dir.no}</small>
              <h4>{dir.label}</h4>
              <p>{dir.desc}</p>
              <div className="rg-route-home-chips">
                {dir.chips.map((chip) => (
                  <span key={chip}>{chip}</span>
                ))}
              </div>
              <span>Открыть направление →</span>
            </button>
          ))}
        </div>
      ) : (
        <>
          {!embeddedInGateway ? (
            <button type="button" className="rg-btn" onClick={handleBack} style={{ marginBottom: 16 }}>
              {level === 'form'
                ? direction === 'social'
                  ? '← К трём направлениям'
                  : '← К выбору типа'
                : '← К трём направлениям'}
            </button>
          ) : null}

          {level === 'kinds' && direction ? (
            <>
              <div className="rg-step-head" style={{ marginBottom: 14 }}>
                <h3>{directionLabel}</h3>
                <p>Выберите тип публикации — далее откроется форма без повторного выбора.</p>
              </div>
              <div className="rg-kind-grid">
                {kindCards().map((card) => (
                  <button
                    key={card.id}
                    type="button"
                    className="rg-kind-card"
                    style={{ '--rg-accent': card.color } as CSSProperties}
                    onClick={() => openKind(card.id)}
                  >
                    <h4>{card.title}</h4>
                    <p>{card.desc}</p>
                    <span>Заполнить форму →</span>
                  </button>
                ))}
              </div>
            </>
          ) : null}

          {level === 'form' && direction && kind ? (
            <div className="rg-panel">
              {direction === 'academia' && (
                <AcademiaPublisher
                  lockedKind={kind}
                  initialDraft={formDraft}
                  onDraftChange={onFormDraftChange}
                  onSuccess={(msg) => showToast(msg || t('publisher.toast.submitted' as any))}
                />
              )}
              {direction === 'catalog' && (
                <CatalogPublisher
                  lockedKind={kind}
                  initialDraft={formDraft}
                  onDraftChange={onFormDraftChange}
                  onSuccess={(msg) => showToast(msg || t('publisher.toast.submitted' as any))}
                />
              )}
              {direction === 'social' && (
                <SocialHandoffForm
                  lockedKind={kind}
                  initialDraft={formDraft}
                  onDraftChange={onFormDraftChange}
                  onSuccess={() => showToast('Передано SMM-менеджеру')}
                />
              )}
            </div>
          ) : null}
        </>
      )}

      {toastMsg ? <div className="rg-toast">{toastMsg}</div> : null}
    </div>
  );
};
